/* eslint-disable react/require-default-props */
import React from 'react';
import {View} from 'react-native';
import {useNavigation, useRoute} from '@react-navigation/native';
import {get, noop} from 'lodash';

import Checkbox from '/component/base/Checkbox';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {
  REPORT_ELIGIBLE_CLIENT_SUPPORT_FIVE_SIX,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_ONE_TWO,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_SEVEN_NINE,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_THREE_FOUR,
} from '/enum/records.enum';
import {EVENT_NAMES} from '/enum/tealium.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import testProperties from '/util/testing/testProperties';

import {generateOnInfoPress, generateOnPress} from '../SortScreen.util';

import {styles} from './SortItem.style';
interface SortOption {
  key: string;
  label: string;
}
interface SortItemProps {
  item: SortOption;
  sortParam: SortOption;
  setSelectedSortParam: (sort: SortOption) => void;
  onShowInfoPopover: (payload: any) => void;
  lastPaidRank: string;
  index?: number;
  total?: number;
}

export const SortItem: React.FC<SortItemProps> = ({
  item = {},
  sortParam = {},
  setSelectedSortParam = noop,
  onShowInfoPopover = noop,
  lastPaidRank,
  index = undefined,
  total = undefined,
}) => {
  const {tt, ts} = useTranslation('filterScreen.filterItem');

  const navigation = useNavigation();
  const route = useRoute();

  const recordsName = get(route, 'params.recordsName');

  const isSelected = get(item, 'key', '') === get(sortParam, 'key', '');

  const sortDescription = get(item, 'description', null);

  const label = tt(
    `sortOptionLabel-${get(item, 'key', '')}`,
    get(item, 'label', ''),
  );

  const positionText =
    typeof index === 'number' && typeof total === 'number'
      ? `, ${index + 1} of ${total}`
      : '';

  const accessibilityLabel = `${label}${positionText}${
    isSelected ? ', selected' : ''
  }`;

  const onPress = generateOnPress({
    item,
    setSelectedSortParam,
    goBack: navigation.goBack,
    recordsName,
    lastPaidRank,
  });

  const onInfoPress = generateOnInfoPress({item, onShowInfoPopover, tt});
  const sendInfoClickEvent = getTealiumUtilDefaultInstance().generateTrackEvent(
    {eventName: EVENT_NAMES.informationClick},
  );
  const handleInfoPress = () => {
    onInfoPress();
    sendInfoClickEvent({
      buttonName: 'Sort Info Popover',
      screenName: 'sort screen',
      lastPaidRank: lastPaidRank,
    });
  };

  const CSBReports = [
    REPORT_ELIGIBLE_CLIENT_SUPPORT_ONE_TWO,
    REPORT_ELIGIBLE_CLIENT_SUPPORT_THREE_FOUR,
    REPORT_ELIGIBLE_CLIENT_SUPPORT_FIVE_SIX,
    REPORT_ELIGIBLE_CLIENT_SUPPORT_SEVEN_NINE,
  ];
  return (
    <View style={styles.itemContainer}>
      <TouchableOpacity
        style={styles.sortOptionContainer}
        onPress={onPress}
        testID={`sortItem-option-${get(item, 'key', 'unknown')}`}
        {...testProperties(null, true)}
        accessible={true}
        accessibilityRole={'radio'}
        accessibilityState={{selected: isSelected}}
        accessibilityLabel={accessibilityLabel}
        accessibilityHint="Double tap to select this sort option">
        <Checkbox
          checked={isSelected}
          checkedIconName="radio"
          style={{icon: styles.icon}}
          readonly
        />
        <Text
          testID={`sortItem-label-${get(item, 'key', '')}`}
          allowFontScaling
          maxFontSizeMultiplier={2}
          style={styles.sortLabel}>
          {tt(
            `sortOptionLabel-${get(item, 'key', '')}`,
            get(item, 'label', ''),
          )}
        </Text>
      </TouchableOpacity>
      {sortDescription && (
        <TouchableOpacity
          onPress={handleInfoPress}
          testID={`sortItem-info-button-${get(item, 'key', 'unknown')}`}
          {...testProperties(null, true)}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel={ts(
            get(item, 'label', ''),
            `${get(item, 'label', '')} information`,
          )}
          accessibilityHint="Double tap to view more information">
          {!CSBReports.includes(recordsName) && (
            <Icon
              name="info"
              style={styles.infoIcon}
              accessible={false}
              testID={`sortItem-info-icon-${get(item, 'key', 'unknown')}`}
            />
          )}
        </TouchableOpacity>
      )}
    </View>
  );
};
